import { useState, useCallback } from 'react';
import { Mail, Phone, Linkedin, CircleHelp as HelpCircle, Zap, Loader as Loader2, Target, ArrowRight, Copy, Check } from 'lucide-react';
import type { ContactOutreach, CallBattlecard } from '../../types';
import EditableOutreachCard from '../account/execute/EditableOutreachCard';
import CallBattlecardCard from '../account/execute/CallBattlecardCard';

interface Props {
  outreachList: ContactOutreach[];
  onGenerate?: () => void;
  generating?: boolean;
  onFieldSave?: (outreachId: string, fieldKey: string, value: string) => Promise<void>;
  onBattlecardSave?: (outreachId: string, updated: CallBattlecard) => Promise<void>;
  onAdjustWordRange?: (outreachId: string, fieldKey: string, minWords: number, maxWords: number) => void;
  adjustingField?: string | null;
  onRegenerate?: (outreachId: string) => void;
  regeneratingId?: string | null;
}

export default function ContactMessagingTabs({
  outreachList,
  onGenerate,
  generating,
  onFieldSave,
  onBattlecardSave,
  onAdjustWordRange,
  adjustingField,
  onRegenerate,
  regeneratingId,
}: Props) {
  const [activeUseCase, setActiveUseCase] = useState(0);

  if (outreachList.length === 0) {
    return (
      <div className="text-center py-10 bg-app-canvas rounded-lg border border-app-line">
        <Mail className="w-10 h-10 text-app-line-strong mx-auto mb-3" />
        <p className="text-sm font-medium text-app-muted">No messaging generated yet</p>
        <p className="text-xs text-app-muted mt-1 mb-5">Generate personalised outreach for this contact</p>
        {onGenerate && (
          <button
            onClick={onGenerate}
            disabled={generating}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-forest-600 text-white text-sm font-semibold rounded-lg hover:bg-forest-700 disabled:opacity-60 transition-colors shadow-none"
          >
            {generating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Zap className="w-4 h-4" />
            )}
            {generating ? 'Generating...' : 'Generate Messaging'}
          </button>
        )}
      </div>
    );
  }

  const outreach = outreachList[activeUseCase];
  if (!outreach) return null;

  const hasIndividualFields = !!(outreach.email_draft || outreach.linkedin_message || outreach.cold_call_script || outreach.why_should_they_care);
  const parsed = !hasIndividualFields && outreach.contact_message
    ? parseContactMessage(outreach.contact_message)
    : null;

  const hasEditing = !!(onFieldSave && onBattlecardSave);
  const isRegenerating = regeneratingId === outreach.id;

  return (
    <div className="space-y-4">
      {outreachList.length > 1 && (
        <div className="flex gap-1 overflow-x-auto pb-1">
          {outreachList.map((o, i) => (
            <button
              key={o.id}
              onClick={() => setActiveUseCase(i)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                i === activeUseCase
                  ? 'bg-forest-100 text-forest-700 border border-forest-200'
                  : 'bg-app-canvas text-app-muted border border-transparent hover:bg-app-canvas'
              }`}
            >
              {o.use_case_title}
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold text-app-muted uppercase tracking-wider">{outreach.use_case_title}</p>
        {onRegenerate && (
          <button
            onClick={() => onRegenerate(outreach.id)}
            disabled={isRegenerating}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-forest-700 bg-forest-50 border border-forest-200 rounded-lg hover:bg-forest-100 disabled:opacity-50 transition-colors"
          >
            {isRegenerating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
            {isRegenerating ? 'Regenerating...' : 'Regenerate All'}
          </button>
        )}
      </div>

      {hasEditing ? (
        <EditableContent
          outreach={outreach}
          parsed={parsed}
          onFieldSave={onFieldSave!}
          onBattlecardSave={onBattlecardSave!}
          onAdjustWordRange={onAdjustWordRange}
          adjustingField={adjustingField}
        />
      ) : (
        <ReadOnlyContent outreach={outreach} parsed={parsed} />
      )}
    </div>
  );
}

function EditableContent({ outreach, parsed, onFieldSave, onBattlecardSave, onAdjustWordRange, adjustingField }: {
  outreach: ContactOutreach;
  parsed: ParsedMessage | null;
  onFieldSave: (outreachId: string, fieldKey: string, value: string) => Promise<void>;
  onBattlecardSave: (outreachId: string, updated: CallBattlecard) => Promise<void>;
  onAdjustWordRange?: (outreachId: string, fieldKey: string, minWords: number, maxWords: number) => void;
  adjustingField?: string | null;
}) {
  const handleDirtyChange = useCallback((_fieldKey: string, _dirty: boolean) => {}, []);

  const handleFieldSave = async (fieldKey: string, value: string) => {
    await onFieldSave(outreach.id, fieldKey, value);
  };

  const handleBattlecardSave = async (updated: CallBattlecard) => {
    await onBattlecardSave(outreach.id, updated);
  };

  const handleAdjust = onAdjustWordRange
    ? (fieldKey: string, min: number, max: number) => onAdjustWordRange(outreach.id, fieldKey, min, max)
    : undefined;

  const whyCare = outreach.why_should_they_care || parsed?.whyCare || '';
  const email = outreach.email_draft || parsed?.email || '';
  const linkedin = outreach.linkedin_message || parsed?.linkedin || '';
  const callScript = outreach.cold_call_script || parsed?.callScript || '';

  return (
    <div className="space-y-4">
      {whyCare && <WhyTheyCareBullets content={whyCare} />}

      {outreach.red_thread_summary && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-app-canvas border border-app-line rounded-lg">
          <ArrowRight className="w-3.5 h-3.5 text-app-muted flex-shrink-0" />
          <p className="text-[11px] text-app-muted leading-relaxed">
            <span className="font-semibold text-app-muted">Red Thread:</span> {outreach.red_thread_summary}
          </p>
        </div>
      )}

      {outreach.call_battlecard ? (
        <CallBattlecardCard
          battlecard={outreach.call_battlecard}
          onSave={handleBattlecardSave}
          hasUnsavedChanges={handleDirtyChange}
        />
      ) : callScript ? (
        <EditableOutreachCard
          title="Cold Call Script"
          icon={<Phone className="w-4 h-4 text-white" />}
          fieldKey="cold_call_script"
          content={callScript}
          headerColor="bg-app-positive"
          cardColor="bg-app-positive/10 border-app-positive"
          onSave={handleFieldSave}
          hasUnsavedChanges={handleDirtyChange}
          expandable
        />
      ) : null}

      {linkedin && (
        <EditableOutreachCard
          title="LinkedIn Message"
          icon={<Linkedin className="w-4 h-4 text-white" />}
          fieldKey="linkedin_message"
          content={linkedin}
          headerColor="bg-app-focus"
          cardColor="bg-app-focus/10 border-app-focus/30"
          onSave={handleFieldSave}
          hasUnsavedChanges={handleDirtyChange}
          showWordControls
          onAdjustToWordRange={handleAdjust}
          adjustingWords={adjustingField === 'linkedin_message'}
          charLimit={300}
        />
      )}

      {email && (
        <EditableOutreachCard
          title="Email Draft"
          icon={<Mail className="w-4 h-4 text-white" />}
          fieldKey="email_draft"
          content={email}
          headerColor="bg-app-focus"
          cardColor="bg-app-focus/10 border-app-focus"
          onSave={handleFieldSave}
          hasUnsavedChanges={handleDirtyChange}
          showWordControls
          onAdjustToWordRange={handleAdjust}
          adjustingWords={adjustingField === 'email_draft'}
        />
      )}

      {outreach.discovery_questions && outreach.discovery_questions.length > 0 && (
        <div className="rounded-lg border border-app-line bg-app-surface shadow-none overflow-hidden">
          <div className="bg-app-ink px-5 py-3 flex items-center gap-2">
            <HelpCircle className="w-4 h-4 text-white" />
            <h4 className="text-sm font-semibold text-white">Discovery Questions</h4>
          </div>
          <div className="p-5 space-y-3">
            {outreach.discovery_questions.map((q, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-app-canvas text-app-muted flex items-center justify-center text-xs font-bold">
                  {i + 1}
                </span>
                <p className="text-sm text-app-ink leading-relaxed">{q}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ReadOnlyContent({ outreach, parsed }: {
  outreach: ContactOutreach;
  parsed: ParsedMessage | null;
}) {
  const whyCare = outreach.why_should_they_care || parsed?.whyCare || '';
  const email = outreach.email_draft || parsed?.email || '';
  const linkedin = outreach.linkedin_message || parsed?.linkedin || '';
  const callScript = outreach.cold_call_script || parsed?.callScript || '';

  return (
    <div className="space-y-4">
      {whyCare && <WhyTheyCareBullets content={whyCare} />}

      {outreach.red_thread_summary && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-app-canvas border border-app-line rounded-lg">
          <ArrowRight className="w-3.5 h-3.5 text-app-muted flex-shrink-0" />
          <p className="text-[11px] text-app-muted leading-relaxed">
            <span className="font-semibold text-app-muted">Red Thread:</span> {outreach.red_thread_summary}
          </p>
        </div>
      )}

      {email && <ReadOnlyCard icon={Mail} title="Email Draft" content={email} accent="blue" />}
      {linkedin && <ReadOnlyCard icon={Linkedin} title="LinkedIn Message" content={linkedin} accent="sky" />}
      {callScript && <ReadOnlyCard icon={Phone} title="Call Script" content={callScript} accent="emerald" />}

      {outreach.discovery_questions && outreach.discovery_questions.length > 0 && (
        <div className="bg-app-canvas rounded-lg border border-app-line p-4">
          <div className="flex items-center gap-2 mb-3">
            <HelpCircle className="w-4 h-4 text-app-muted" />
            <h4 className="text-xs font-bold text-app-muted uppercase tracking-wider">Discovery Questions</h4>
          </div>
          <div className="space-y-2">
            {outreach.discovery_questions.map((q, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-app-line text-app-muted flex items-center justify-center text-[10px] font-bold">{i + 1}</span>
                <p className="text-sm text-app-ink leading-relaxed">{q}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function WhyTheyCareBullets({ content }: { content: string }) {
  const [copied, setCopied] = useState(false);
  const bullets = content
    .split('\n')
    .map(line => line.replace(/^[\s\-*\u2022\u25CF\u25CB]+/, '').trim())
    .filter(line => line.length > 0);

  if (bullets.length === 0) return null;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="rounded-lg border border-app-warning bg-app-warning/10 shadow-none overflow-hidden">
      <div className="bg-app-warning px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-white" />
          <h4 className="text-sm font-semibold text-white">Why Should They Care</h4>
        </div>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-white/80 hover:text-white hover:bg-app-surface/10 rounded transition-colors"
        >
          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <div className="p-5">
        <ul className="space-y-2.5">
          {bullets.map((bullet, i) => (
            <li key={i} className="flex items-start gap-2.5">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-app-warning flex-shrink-0" />
              <span className="text-sm font-medium text-app-warning leading-relaxed">{bullet}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

const ACCENT_MAP: Record<string, { bg: string; border: string; header: string }> = {
  blue: { bg: 'bg-app-focus/10', border: 'border-app-focus', header: 'bg-app-focus' },
  sky: { bg: 'bg-app-focus/10', border: 'border-app-focus/30', header: 'bg-app-focus' },
  emerald: { bg: 'bg-app-positive/10', border: 'border-app-positive', header: 'bg-app-positive' },
};

function ReadOnlyCard({ icon: Icon, title, content, accent }: {
  icon: typeof Mail;
  title: string;
  content: string;
  accent: string;
}) {
  const [copied, setCopied] = useState(false);
  const colors = ACCENT_MAP[accent] || ACCENT_MAP.blue;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={`rounded-lg border shadow-none overflow-hidden ${colors.bg} ${colors.border}`}>
      <div className={`${colors.header} px-5 py-3 flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-white" />
          <h4 className="text-sm font-semibold text-white">{title}</h4>
        </div>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-white/80 hover:text-white hover:bg-app-surface/10 rounded transition-colors"
        >
          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <div className="p-5">
        <p className="text-sm text-app-ink leading-relaxed whitespace-pre-wrap">{content}</p>
      </div>
    </div>
  );
}

interface ParsedMessage {
  whatTheyDo: string;
  whyCare: string;
  whatResonate: string;
  email: string;
  linkedin: string;
  callScript: string;
}

const SECTION_PATTERNS: { key: keyof ParsedMessage; patterns: RegExp[] }[] = [
  { key: 'whatTheyDo', patterns: [/^WHAT THEY DO$/i, /^THEIR ROLE$/i] },
  { key: 'whyCare', patterns: [/^WHY THEY (?:SHOULD )?CARE$/i, /^WHY CARE$/i] },
  { key: 'whatResonate', patterns: [/^WHAT WILL RESONATE$/i, /^WHAT RESONATES$/i] },
  { key: 'email', patterns: [/^EMAIL(?:\s+DRAFT)?$/i] },
  { key: 'linkedin', patterns: [/^LINKEDIN(?:\s+MESSAGE)?$/i] },
  { key: 'callScript', patterns: [/^(?:COLD\s+)?CALL(?:\s+SCRIPT)?$/i] },
];

function parseContactMessage(text: string): ParsedMessage {
  const result: ParsedMessage = { whatTheyDo: '', whyCare: '', whatResonate: '', email: '', linkedin: '', callScript: '' };
  const lines = text.split('\n');
  let currentKey: keyof ParsedMessage | null = null;
  let buffer: string[] = [];

  const flush = () => {
    if (currentKey) result[currentKey] = buffer.join('\n').trim();
    buffer = [];
  };

  for (const line of lines) {
    const trimmed = line.trim();
    const matched = SECTION_PATTERNS.find(s => s.patterns.some(p => p.test(trimmed)));
    if (matched) {
      flush();
      currentKey = matched.key;
    } else {
      buffer.push(line);
    }
  }
  flush();
  return result;
}
